import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Alert } from "antd";

import { useAssessEngagementV2 } from "../../../hooks/useAssessEngagementV2";
import { useAssessEngagementsBatchV2 } from "../../../hooks/useAssessEngagementsBatchV2";
import { useAssessmentJobPoller } from "../../../hooks/useAssessmentJobPoller";
import { AIModalState } from "../../../enums/CompassAI";
import { HeroKind } from "../../../enums/Modal";
import { MESSAGES } from "../../../messages";
import {
  BatchAssessmentV2Result,
  BatchAssessEngagementV2Request,
} from "../../../types/engagementAssessmentV2Types";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { JobProgressBar } from "./JobProgressBar";
import { CompassWhiteButton } from "../../buttons/styledComponents";

import { darkGreen, errorRed, greyText } from "../../../styles";

import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import engagementRatingBg from "../../../assets/images/engagement_rating.png";
import aiJobRunningAnimation from "../../../assets/svg/ai_job_running_animation.svg";

const heroCopy = MESSAGES.COMPASS_AI.ENGAGEMENT_ASSESSMENT;
const v2Errors = MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS;

type ModalContext = { dotCounter: number };

const ASSESSMENT_TRIGGER_V2_MODAL_CONFIG = defineModalConfig<
  AIModalState,
  ModalContext
>({
  title: heroCopy.MODAL_TITLE,
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    [AIModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementRatingBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: heroCopy.TITLE.IDLE,
        description: heroCopy.DESCRIPTION.IDLE,
      },
    },
    [AIModalState.ANALYZING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: heroCopy.TITLE.ANALYZING,
        description: heroCopy.DESCRIPTION.ANALYZING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.POLLING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: heroCopy.TITLE.POLLING,
        description: heroCopy.DESCRIPTION.POLLING,
      },
    },
    [AIModalState.SUCCESS]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementRatingBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: heroCopy.TITLE.SUCCESS,
        description: heroCopy.DESCRIPTION.SUCCESS,
      },
    },
    [AIModalState.ERROR]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementRatingBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: heroCopy.TITLE.ERROR,
        titleColor: errorRed,
        accentColor: errorRed,
        description: heroCopy.DESCRIPTION.ERROR,
      },
    },
  },
});

const SuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IdList = styled.ul`
  margin: 0;
  padding-left: 20px;
  max-height: 160px;
  overflow-y: auto;
  color: ${darkGreen};
  font-size: 12px;
  li {
    margin: 2px 0;
    word-break: break-all;
  }
`;

export type AssessmentTriggerModalV2Props = {
  mode: "single" | "batch";
  engagementRecordId?: string; // required when mode = "single"
  engagementRecordIds?: string[]; // batch mode
  managerIds?: string[]; // batch mode
  compassCids?: string[]; // batch mode
  onSingleComplete?: (engagementRecordId: string) => void;
  onBatchComplete?: (result: BatchAssessmentV2Result) => void;
  onRunningChange?: (running: boolean) => void;
  onClose: () => void;
  show: boolean;
};

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const SUCCESSFUL_KEYS = [
  "successful_ids",
  "successful_engagement_record_ids",
  "successful_engagement_ids",
  "succeeded_ids",
] as const;

const FAILED_KEYS = [
  "failed_ids",
  "failed_engagement_record_ids",
  "failed_engagement_ids",
] as const;

const extractBatchResult = (
  jobId: string,
  payload: unknown,
): BatchAssessmentV2Result => {
  if (payload === null || typeof payload !== "object") {
    return { jobId, successfulIds: [], failedIds: [] };
  }
  const bag = payload as Record<string, unknown>;
  const successfulIds =
    SUCCESSFUL_KEYS.map((k) => asStringArray(bag[k])).find(
      (arr) => arr.length > 0,
    ) ?? [];
  const failedIds =
    FAILED_KEYS.map((k) => asStringArray(bag[k])).find(
      (arr) => arr.length > 0,
    ) ?? [];
  return { jobId, successfulIds, failedIds };
};

export const AssessmentTriggerModalV2: React.FC<
  AssessmentTriggerModalV2Props
> = ({
  mode,
  engagementRecordId,
  engagementRecordIds,
  managerIds,
  compassCids,
  onSingleComplete,
  onBatchComplete,
  onRunningChange,
  onClose,
  show,
}) => {
  const [dotCounter, setDotCounter] = useState(0);
  const [isTriggering, setIsTriggering] = useState(false);

  const singleTrigger = useAssessEngagementV2();
  const batchTrigger = useAssessEngagementsBatchV2();
  const poller = useAssessmentJobPoller<Record<string, unknown>>();

  const singlePayload = useMemo(
    () =>
      engagementRecordId
        ? { engagement_record_id: engagementRecordId }
        : null,
    [engagementRecordId],
  );

  const batchPayload = useMemo<BatchAssessEngagementV2Request | null>(() => {
    const payload: BatchAssessEngagementV2Request = {};
    if (engagementRecordIds && engagementRecordIds.length > 0) {
      payload.engagement_record_ids = engagementRecordIds;
    }
    if (managerIds && managerIds.length > 0) {
      payload.manager_id = managerIds;
    }
    if (compassCids && compassCids.length > 0) {
      payload.compass_cid = compassCids;
    }
    return Object.keys(payload).length > 0 ? payload : null;
  }, [engagementRecordIds, managerIds, compassCids]);

  const completionFiredRef = useRef(false);

  const modalState: AIModalState = useMemo(() => {
    if (isTriggering) return AIModalState.ANALYZING;
    switch (poller.state.kind) {
      case "queued":
        return AIModalState.ANALYZING;
      case "running":
        return AIModalState.POLLING;
      case "success":
        return AIModalState.SUCCESS;
      case "failed":
      case "timeout":
        return AIModalState.ERROR;
      case "cancelled_locally":
      case "idle":
      default:
        return AIModalState.IDLE;
    }
  }, [isTriggering, poller.state.kind]);

  const isRunning =
    modalState === AIModalState.ANALYZING ||
    modalState === AIModalState.POLLING;
  useEffect(() => {
    onRunningChange?.(isRunning);
  }, [isRunning, onRunningChange]);

  useEffect(() => {
    if (modalState !== AIModalState.ANALYZING) return;
    const interval = setInterval(() => {
      setDotCounter((prev) => (prev + 1) % 4);
    }, 500);
    return (): void => clearInterval(interval);
  }, [modalState]);

  const handleStart = useCallback(async (): Promise<void> => {
    completionFiredRef.current = false;
    setIsTriggering(true);
    try {
      let jobId: string | null = null;

      if (mode === "single") {
        if (!singlePayload) {
          setIsTriggering(false);
          return;
        }
        singleTrigger.reset();
        jobId = await singleTrigger.triggerAssessment(singlePayload);
      } else {
        if (!batchPayload) {
          setIsTriggering(false);
          return;
        }
        batchTrigger.reset();
        jobId = await batchTrigger.triggerBatchAssessment(batchPayload);
      }

      if (jobId) {
        poller.startPolling(jobId);
      }
    } finally {
      setIsTriggering(false);
    }
  }, [mode, singlePayload, batchPayload, singleTrigger, batchTrigger, poller]);

  useEffect(() => {
    if (poller.state.kind !== "success") return;
    if (completionFiredRef.current) return;
    completionFiredRef.current = true;

    if (mode === "single" && engagementRecordId) {
      onSingleComplete?.(engagementRecordId);
    } else if (mode === "batch") {
      const result = extractBatchResult(
        poller.state.jobId,
        poller.state.result,
      );
      onBatchComplete?.(result);
    }
  }, [
    poller.state,
    mode,
    engagementRecordId,
    onSingleComplete,
    onBatchComplete,
  ]);

  const handleClose = useCallback((): void => {
    poller.stopPolling();
    setDotCounter(0);
    setIsTriggering(false);
    onClose();
  }, [poller, onClose]);

  const { modalProps, isProcessing } = useCompassModal({
    config: ASSESSMENT_TRIGGER_V2_MODAL_CONFIG,
    state: modalState,
    context: { dotCounter },
  });

  const batchResultForRender = useMemo<BatchAssessmentV2Result | null>(() => {
    if (mode !== "batch") return null;
    if (poller.state.kind !== "success") return null;
    return extractBatchResult(poller.state.jobId, poller.state.result);
  }, [mode, poller.state]);

  const canStart =
    mode === "single" ? singlePayload !== null : batchPayload !== null;

  const renderFooter = (): React.ReactNode => {
    if (modalState === AIModalState.SUCCESS) {
      return (
        <ModalFooter showDivider={false}>
          <CompassWhiteButton onClick={handleClose}>Done</CompassWhiteButton>
        </ModalFooter>
      );
    }

    if (modalState === AIModalState.IDLE) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Cancel"
          submitText={heroCopy.START_ASSESSMENT}
          onSubmit={handleStart}
          submitDisabled={!canStart}
          showDivider={false}
        />
      );
    }

    if (modalState === AIModalState.ERROR) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Close"
          submitText="Retry"
          onSubmit={handleStart}
          submitDisabled={!canStart}
          showDivider={false}
        />
      );
    }

    return (
      <ModalFooter
        onClose={handleClose}
        closeText="Cancel"
        hideSubmit
        isLoading={isProcessing}
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>
        {modalState === AIModalState.POLLING &&
          poller.state.kind === "running" && (
            <JobProgressBar progress={poller.state.progress} />
          )}

        {modalState === AIModalState.SUCCESS &&
          mode === "batch" &&
          batchResultForRender && (
            <SuccessBlock>
              {batchResultForRender.successfulIds.length > 0 && (
                <div>
                  <CompassParagraph bold color={darkGreen}>
                    Successful ({batchResultForRender.successfulIds.length})
                  </CompassParagraph>
                  <IdList>
                    {batchResultForRender.successfulIds.map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </IdList>
                </div>
              )}

              {batchResultForRender.failedIds.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={v2Errors.BATCH_PARTIAL_FAILURE(
                    batchResultForRender.failedIds.length,
                  )}
                  description={
                    <IdList>
                      {batchResultForRender.failedIds.map((id) => (
                        <li key={id}>{id}</li>
                      ))}
                    </IdList>
                  }
                />
              )}

              {batchResultForRender.successfulIds.length === 0 &&
                batchResultForRender.failedIds.length === 0 && (
                  <CompassParagraph color={greyText}>
                    Batch assessment completed.
                  </CompassParagraph>
                )}
            </SuccessBlock>
          )}
      </ModalContent>
    </CompassModal>
  );
};
